import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQuery } from '@tanstack/react-query';
import { useIsFocused } from 'expo-router/react-navigation';
import { useEffect, useRef } from 'react';
import { View } from 'react-native';

import { CommunityDisclaimer } from './CommunityDisclaimerModal';

import { useAbility } from '@/hooks/useAbility';
import { useAbilityGate } from '@/hooks/useAbilityGate';
import { useCommunityAuthModalShown } from '@/hooks/useCommunityAuthModalShown';
import { useToaster } from '@/hooks/useToaster';
import { getCancerTypesV2QueryOptions } from '@/services/getCancerTypesV2';

export type CommunityWrapperProps = {
  children: React.ReactNode;
};

/**
 * Community Wrapper
 * Responsibilities:
 * - Render NPI verification modal for unverified web users
 * - Show disclaimer modal on first visit
 * - Gate a restricted web reader's interactions behind one request for access
 */
export function CommunityWrapper({ children }: CommunityWrapperProps) {
  const { isError } = useQuery(getCancerTypesV2QueryOptions());

  /**
   * Unconfirmed accounts keep `preview` on posts and lose `read`, so this is
   * what separates a reader from someone who must verify first.
   */
  const canReadPosts = useAbility().can('read', 'Post');

  const showAuthModal = useCommunityAuthModalShown();

  const { requestAccess } = useAbilityGate();

  const isFocused = useIsFocused();

  const toast = useToaster();

  useEffect(() => {
    if (isError) {
      toast.showError({
        title: 'There was an error loading this inquiry. Please try again.',
      });
    }
  }, [isError]);

  const contentRef = useRef<any>(null);

  /**
   * On web a restricted reader's every interaction is swallowed here and turned into one
   * request for access. One tap fires several of the captured events, and when the request
   * is a navigation (confirming account details) rather than the modal, nothing else stops
   * the next event, so the gate asks once per attachment. It detaches while the screen is
   * out of focus, so coming back re-arms it.
   */
  useEffect(() => {
    const needsGate = isWeb && !canReadPosts;

    const el = contentRef.current as HTMLElement | null;

    if (!needsGate || showAuthModal || !isFocused || !el) {
      return;
    }

    let hasRequestedAccess = false;

    const blockAndOpen = (e: Event) => {
      try {
        e.preventDefault();

        // @ts-ignore
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }

        e.stopPropagation();
      } catch {}

      if (!hasRequestedAccess) {
        hasRequestedAccess = true;

        requestAccess();
      }
    };

    const types: (keyof DocumentEventMap)[] = ['pointerdown', 'keydown', 'focusin', 'click'];

    types.forEach((t) => el.addEventListener(t as any, blockAndOpen as EventListener, true));

    return () => {
      types.forEach((t) => el.removeEventListener(t as any, blockAndOpen as EventListener, true));
    };
  }, [canReadPosts, showAuthModal, isFocused, requestAccess]);

  return (
    <View ref={contentRef} className="mx-auto w-full flex-1 flex-row web:max-w-screen-2xl">
      {children}

      <CommunityDisclaimer />
    </View>
  );
}
